import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { TID } from "../../lib/testIds";

// Swap in real project loops by editing this array only.
const WORK_ITEMS = [
    {
        number: "01",
        title: "Brand Launch Film",
        client: "Placeholder Client",
        videoSrc: "/work/loop-1.mp4",
        posterSrc: "/work/poster-1.jpg",
        liveUrl: "#",
    },
    {
        number: "02",
        title: "Kinetic Type Reel",
        client: "Placeholder Client",
        videoSrc: "/work/loop-2.mp4",
        posterSrc: "/work/poster-2.jpg",
        liveUrl: "#",
    },
    {
        number: "03",
        title: "Product Animation",
        client: "Placeholder Client",
        videoSrc: "/work/loop-3.mp4",
        posterSrc: "/work/poster-3.jpg",
        liveUrl: "#",
    },
    {
        number: "04",
        title: "Data Story",
        client: "Placeholder Client",
        videoSrc: "/work/loop-4.mp4",
        posterSrc: "/work/poster-4.jpg",
        liveUrl: "#",
    },
];

const EASE = [0.25, 0.1, 0.25, 1];
const MARQUEE_INITIAL = { opacity: 0, y: 40 };
const MARQUEE_IN_VIEW = { opacity: 1, y: 0 };
const MARQUEE_VIEWPORT = { once: true, margin: "-60px" };
const MARQUEE_TRANSITION = { duration: 0.7, ease: EASE };

export function SelectedWorkSection() {
    const reducedMotion = usePrefersReducedMotion();
    return (
        <section data-testid={TID.selectedWorkSection} className="relative" style={{ background: "#0A0A0A" }}>
            <div className="w-full mx-auto px-5 sm:px-8 md:px-10 max-w-[1440px]">
                <div
                    data-testid={TID.selectedWorkLabel}
                    className="text-base uppercase tracking-[0.3em] mb-8 md:mb-12 text-left"
                    style={{ color: "var(--accent)" }}
                >
                    SEE WHAT WE&apos;VE CREATED
                </div>
            </div>
            <motion.div
                initial={MARQUEE_INITIAL}
                whileInView={MARQUEE_IN_VIEW}
                viewport={MARQUEE_VIEWPORT}
                transition={MARQUEE_TRANSITION}
                className={`work-marquee overflow-x-clip ${reducedMotion ? "overflow-x-auto" : ""}`}
            >
                <div className={`flex w-max ${reducedMotion ? "" : "work-marquee-track"}`}>
                    {[0, 1].map((copy) => (
                        <div key={copy} className="flex" aria-hidden={copy === 1}>
                            {WORK_ITEMS.map((item, i) => (
                                <div
                                    key={`${item.number}-${copy}`}
                                    className="flex-none w-[85vw] sm:w-[520px] md:w-[640px] mr-4 md:mr-6"
                                >
                                    <WorkCard item={item} index={i} isDuplicate={copy === 1} />
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </motion.div>
        </section>
    );
}

function usePrefersReducedMotion() {
    const [reduced, setReduced] = useState(false);
    useEffect(() => {
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
        setReduced(mq.matches);
        const onChange = (e) => setReduced(e.matches);
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
        // Mount-once: mq and onChange are created inside the effect.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return reduced;
}

function WorkCard({ item, index, isDuplicate = false }) {
    const cardRef = useRef(null);
    const videoRef = useRef(null);
    const loadedRef = useRef(false);
    const [failed, setFailed] = useState(false);
    const [ready, setReady] = useState(false);
    const reducedMotion = usePrefersReducedMotion();

    useEffect(() => {
        if (reducedMotion || failed) return;
        const card = cardRef.current;
        if (!card) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                const video = videoRef.current;
                if (!video) return;
                if (entry.isIntersecting) {
                    if (!loadedRef.current) {
                        video.src = item.videoSrc;
                        loadedRef.current = true;
                    }
                    video.muted = true; // property required for Safari/iOS autoplay
                    video.play().catch(() => {});
                } else if (loadedRef.current) {
                    video.pause();
                }
            },
            { threshold: 0.25 }
        );
        observer.observe(card);
        return () => observer.disconnect();
        // IntersectionObserver is a browser global; refs are stable.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item.videoSrc, reducedMotion, failed]);

    const showPosterOnly = reducedMotion || failed;

    return (
        <motion.div
            ref={cardRef}
            data-testid={isDuplicate ? undefined : TID.selectedWorkCard(index)}
            whileHover={{ scale: 1.02 }}
            className="group relative overflow-hidden rounded-[24px] border aspect-[16/10] cursor-pointer"
            style={{ borderColor: "var(--line)", background: "#0C0C0C", transition: "transform 0.4s ease-out" }}
        >
            {showPosterOnly ? (
                <>
                    <img
                        src={item.posterSrc}
                        alt={item.title}
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="lazy"
                    />
                    {reducedMotion && !failed && (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div
                                className="w-16 h-16 rounded-full border-2 flex items-center justify-center backdrop-blur-md"
                                style={{ borderColor: "var(--line)", background: "rgba(0,0,0,0.4)" }}
                            >
                                <Play size={22} style={{ color: "var(--text)" }} fill="currentColor" />
                            </div>
                        </div>
                    )}
                </>
            ) : (
                <>
                    <video
                        ref={videoRef}
                        muted
                        loop
                        playsInline
                        autoPlay
                        preload="metadata"
                        poster={item.posterSrc}
                        onLoadedData={() => setReady(true)}
                        onError={() => setFailed(true)}
                        className="absolute inset-0 w-full h-full object-cover transition-[filter] duration-500 group-hover:brightness-110"
                    />
                    {/* loading skeleton — fades out once the video has data */}
                    <div
                        aria-hidden
                        data-testid={isDuplicate ? undefined : `selected-work-skeleton-${index}`}
                        className={`absolute inset-0 pointer-events-none transition-opacity duration-500 ${ready ? "opacity-0" : "opacity-100"}`}
                    >
                        <div className="absolute inset-0 work-skeleton-shimmer" />
                    </div>
                </>
            )}

            {/* readability gradient */}
            <div
                className="absolute inset-x-0 bottom-0 h-2/3 pointer-events-none"
                style={{ background: "linear-gradient(180deg, transparent, rgba(0,0,0,0.6))" }}
            />

            {/* bottom-left intentionally empty */}
        </motion.div>
    );
}
